// lib/server-utils/cookie-manager.ts


import crypto from 'crypto';
import { cookies, headers } from '@/lib/stubs';
import { encryptionService } from './encryption';
import type { UserRole } from '../types/auth.types';

/* =============================================================================
 * COOKIE MANAGER (Next.js App Router) - P0 FIXES APPLIED
 * -----------------------------------------------------------------------------
 * CRITICAL FIXES:
 * ✅ Role cookie is now encrypted when writing
 * ✅ Safe decrypt-if-encrypted logic for reading
 * ✅ Backward compatibility with plaintext cookies
 * ✅ Never throws - always returns null/empty on errors
 * ============================================================================= */

const DEV = process.env.NODE_ENV === 'development';
const TAG = '[SERVER][CookieManager]';

/* =============================================================================
 * TYPES
 * ============================================================================= */

export interface SessionMetadata {
  id: string;
  userId: string;
  createdAt: number;
  lastActive: number;
  deviceId: string;
  deviceName: string;
  ipAddress?: string;
  userAgent?: string;
  location?: string;
  expiresAt: number;
  isValid: boolean;
  role?: UserRole;
}

export interface AuthCookies {
  access_token?: string;
  refresh_token?: string;
  user_id?: string;
  role?: string;  // Can be encrypted or plaintext (for backward compatibility)
  session_id?: string;
  device_id?: string;
  device_name?: string;
  remember_me?: string;
  auth_state?: string;
  session_meta?: string;
  session_expires_at?: string;
}

interface CookieGet {
  name: string;
  value: string;
}

interface CookieStoreRead {
  get(name: string): CookieGet | undefined;
}

interface CookieStoreWrite extends CookieStoreRead {
  set(name: string, value: string, options?: CookieOptions): void;
  delete(name: string): void;
}

type SameSite = 'lax' | 'strict' | 'none';

export interface CookieOptions {
  httpOnly?: boolean;
  secure?: boolean;
  sameSite?: SameSite;
  path?: string;
  domain?: string;
  maxAge?: number;
  expires?: Date;
  priority?: 'low' | 'medium' | 'high';
}

interface HeaderStoreRead {
  get(name: string): string | null;
}

/* =============================================================================
 * CONFIGURATION
 * ============================================================================= */

const SECURITY_CONFIG = {
  ACCESS_TOKEN_MAX_AGE: 15 * 60, // 15 minutes
  REFRESH_TOKEN_MAX_AGE: 7 * 24 * 60 * 60, // 7 days
  REFRESH_TOKEN_MAX_AGE_REMEMBER: 30 * 24 * 60 * 60, // 30 days
  SESSION_ID_LENGTH: 32,
  DEVICE_ID_LENGTH: 24,
  COOKIE_BASE_OPTIONS: {
    httpOnly: true,
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'lax' as const,
    path: '/',
    priority: 'high' as const,
  },
  ENCRYPTION_IV_BYTES: 12,
  ENCRYPTION_TAG_BYTES: 16,
  MAX_REFRESH_ATTEMPTS: 3,
  REFRESH_COOLDOWN_MS: 10_000,
  SESSION_TIMEOUT_MINUTES: 30,
  TRANSIENT_ERROR_MAX_RETRIES: 3,
  TRANSIENT_ERROR_BACKOFF_MS: 1_000,
  COOKIE_UPDATE_COOLDOWN_MS: 1_000, // Prevent rapid cookie updates
} as const;

const COOKIE_NAMES = {
  ACCESS: 'access_token',
  REFRESH: 'refresh_token',
  USER_ID: 'user_id',
  ROLE: 'user_role', // NOW ENCRYPTED
  SESSION_ID: 'session_id',
  DEVICE_ID: 'device_id',
  DEVICE_NAME: 'device_name',
  REMEMBER: 'remember_me',
  AUTH_STATE: 'auth_state',
  SESSION_META: 'session_meta',
  SESSION_EXPIRES_AT: 'session_expires_at',
} as const;

type CookieName = (typeof COOKIE_NAMES)[keyof typeof COOKIE_NAMES];

const TRANSIENT_COOKIE_NAMES = {
  TRANSIENT_ERROR_COUNT: 'transient_error_count',
  LAST_TRANSIENT_ERROR: 'last_transient_error',
  RETRY_AFTER: 'retry_after',
  LAST_COOKIE_UPDATE: 'last_cookie_update',
} as const;

/* =============================================================================
 * LOGGING UTILITIES
 * ============================================================================= */

function safeJson(payload: unknown) {
  try {
    return payload ? JSON.parse(JSON.stringify(payload)) : undefined;
  } catch {
    return undefined;
  }
}

function log(event: string, payload?: Record<string, unknown>) {
  if (!DEV) return;
  console.log(`${TAG} ${event}`, payload ? safeJson(payload) : '');
}

function warn(event: string, payload?: Record<string, unknown>) {
  if (!DEV) return;
  console.warn(`${TAG} ⚠️ ${event}`, payload ? safeJson(payload) : '');
}

function errLog(event: string, payload?: Record<string, unknown>) {
  console.error(`${TAG} ❌ ${event}`, payload ? safeJson(payload) : '');
}

function mask(v?: string) {
  if (!v) return '';
  const s = String(v);
  if (s.length <= 10) return '***';
  return `${s.slice(0, 4)}…${s.slice(-4)}`;
}

/* =============================================================================
 * ENHANCED ERROR CLASSIFICATION
 * ============================================================================= */

export type ErrorSeverity = 'auth' | 'transient' | 'permanent' | 'unknown';

export interface ErrorClassification {
  severity: ErrorSeverity;
  requiresLogout: boolean;
  isTransient: boolean;
  httpStatus?: number;
  code?: string;
}

export function classifyError(error: unknown): ErrorClassification {
  function getHttpStatus(err: unknown): number | undefined {
    if (!err || typeof err !== 'object') return undefined;
    
    const anyErr = err as Record<string, any>;
    
    // Check Axios error structure
    if (anyErr.isAxiosError) {
      return anyErr.response?.status || anyErr.status;
    }
    
    // Check standardized error structure
    const status = anyErr.status ?? anyErr.statusCode;
    if (typeof status === 'number') return status;
    
    // Check response object
    const responseStatus = anyErr.response?.status;
    if (typeof responseStatus === 'number') return responseStatus;
    
    // Check cause
    const causeStatus = anyErr.cause?.status ?? anyErr.cause?.statusCode;
    if (typeof causeStatus === 'number') return causeStatus;
    
    return undefined;
  }

  const httpStatus = getHttpStatus(error);
  const isUnauthorized = httpStatus === 401 || httpStatus === 419;
  const isForbidden = httpStatus === 403;
  
  // Auth errors require logout
  if (isUnauthorized || isForbidden) {
    return {
      severity: 'auth',
      requiresLogout: true,
      isTransient: false,
      httpStatus,
      code: isUnauthorized ? 'UNAUTHORIZED' : 'FORBIDDEN',
    };
  }

  // Server errors (5xx) are transient
  if (httpStatus !== undefined && httpStatus >= 500 && httpStatus < 600) {
    return {
      severity: 'transient',
      requiresLogout: false,
      isTransient: true,
      httpStatus,
      code: 'SERVER_ERROR',
    };
  }

  const message = String(
    error instanceof Error 
      ? error.message 
      : typeof error === 'string' 
        ? error 
        : JSON.stringify(error ?? '')
  ).toLowerCase();

  // Network/timeout errors are transient
  const isNetworkError = 
    message.includes('network') || 
    message.includes('econn') ||
    message.includes('socket') ||
    message.includes('fetch failed') ||
    message.includes('failed to fetch') ||
    message.includes('network request failed');

  const isTimeout = 
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('etimedout') ||
    message.includes('econnaborted') ||
    message.includes('abort') ||
    message.includes('time-out');

  if (isNetworkError || isTimeout) {
    return {
      severity: 'transient',
      requiresLogout: false,
      isTransient: true,
      httpStatus,
      code: isTimeout ? 'TIMEOUT' : 'NETWORK_ERROR',
    };
  }

  // Other 4xx errors are permanent but don't require logout
  if (httpStatus !== undefined && httpStatus >= 400 && httpStatus < 500) {
    return {
      severity: 'permanent',
      requiresLogout: false,
      isTransient: false,
      httpStatus,
      code: 'CLIENT_ERROR',
    };
  }

  return {
    severity: 'unknown',
    requiresLogout: false,
    isTransient: false,
    httpStatus,
    code: 'UNKNOWN_ERROR',
  };
}

export function shouldHardLogout(error: unknown): boolean {
  return classifyError(error).requiresLogout;
}

export function isTransientError(error: unknown): boolean {
  return classifyError(error).isTransient;
}

/* =============================================================================
 * COOKIE DOMAIN NORMALIZATION - IMPROVED
 * ============================================================================= */

function normalizeCookieDomain(input?: string): string | undefined {
  const raw = (input || '').trim();
  if (!raw) return undefined;

  // Special handling for localhost - allows cookies on all localhost ports
  if (raw.toLowerCase() === 'localhost') {
    return 'localhost';
  }

  // Remove protocol if present
  let domain = raw;
  if (domain.includes('://')) {
    try {
      const url = new URL(domain);
      domain = url.hostname;
    } catch {
      domain = domain.replace(/^(https?:\/\/)?/, '');
    }
  }

  // Remove port if present
  domain = domain.split(':')[0];

  // Remove any paths
  domain = domain.split('/')[0];

  // Clean up
  domain = domain.trim().toLowerCase();

  // Validation - ensure it's a valid domain
  if (!domain || 
      domain.includes(' ') || 
      domain.includes('http') || 
      domain.includes('://') ||
      !/^[a-zA-Z0-9.-]+$/.test(domain)) {
    return undefined;
  }

  // For production, return the domain
  if (process.env.NODE_ENV === 'production' && domain.includes('.')) {
    return domain;
  }

  // For development or invalid domains, don't set domain (host-only cookies)
  return undefined;
}

const ENV_CONFIG = {
  COOKIE_DOMAIN: normalizeCookieDomain(process.env.COOKIE_DOMAIN),
  LOG_SECURITY_EVENTS: process.env.NODE_ENV === 'production',
  USE_SECURE_COOKIES: process.env.NODE_ENV === 'production',
  BASE_PATH: process.env.NEXT_PUBLIC_BASE_PATH || '/',
} as const;

// Log configuration in development
if (DEV) {
  log('Configuration initialized', {
    nodeEnv: process.env.NODE_ENV,
    rawCookieDomain: process.env.COOKIE_DOMAIN,
    normalizedCookieDomain: ENV_CONFIG.COOKIE_DOMAIN || '(host-only cookies)',
    secureCookies: ENV_CONFIG.USE_SECURE_COOKIES,
    basePath: ENV_CONFIG.BASE_PATH,
  });
}

/* =============================================================================
 * LOW-LEVEL HELPERS
 * ============================================================================= */

function nowMs(): number {
  return Date.now();
}

function clampSeconds(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, Math.floor(n)));
}

function randomHexChars(targetLen: number): string {
  const len = Math.max(2, targetLen);
  const bytes = Math.ceil(len / 2);
  return crypto.randomBytes(bytes).toString('hex').slice(0, len);
}

function sanitizeDeviceName(input?: string): string {
  const v = (input || '').trim();
  if (!v) return 'Unknown Device';
  const cleaned = v.replace(/[^\w\s.\-()/]/g, '').trim();
  return cleaned.slice(0, 80) || 'Unknown Device';
}

function parseForwardedFor(v: string | null): string | undefined {
  if (!v) return undefined;
  const first = v.split(',')[0]?.trim();
  return first || undefined;
}

function looksLikeGcmEncrypted(value: string | undefined): boolean {
  if (!value) return false;
  
  // Check if it's already decrypted (plain text)
  if (!value.includes(':')) return false;
  
  const parts = value.split(':');
  if (parts.length !== 3) return false;

  const [ivHex, tagHex, cipherHex] = parts;
  const expectedIvHexLen = SECURITY_CONFIG.ENCRYPTION_IV_BYTES * 2;
  const expectedTagHexLen = SECURITY_CONFIG.ENCRYPTION_TAG_BYTES * 2;

  if (ivHex.length !== expectedIvHexLen) return false;
  if (tagHex.length !== expectedTagHexLen) return false;
  if (!cipherHex.length) return false;

  const combined = ivHex + tagHex + cipherHex;
  return /^[0-9a-fA-F]+$/.test(combined);
}

/* =============================================================================
 * ROLE VALIDATION & ENCRYPTION HELPERS
 * ============================================================================= */

const ALLOWED_ROLES = ['owner', 'admin', 'waiter', 'customer'] as const;
type AllowedRole = typeof ALLOWED_ROLES[number];

function isValidRole(role: string): role is AllowedRole {
  return ALLOWED_ROLES.includes(role as AllowedRole);
}

/**
 * ✅ NEW: Safe role decryption with backward compatibility
 * - If plaintext and matches allow-list → accept
 * - If encrypted → decrypt and validate
 * - If invalid → return null (never throw)
 */
function safeDecryptRole(roleValue: string | undefined): string | null {
  if (!roleValue) return null;
  
  try {
    // Check if it's plaintext (no colon)
    if (!roleValue.includes(':')) {
      // Plaintext - validate against allow-list
      if (isValidRole(roleValue)) {
        if (DEV) {
          console.log(`[CookieManager] Accepted plaintext role: ${roleValue}`);
        }
        return roleValue;
      }
      
      if (DEV) {
        console.warn(`[CookieManager] Rejected invalid plaintext role: ${roleValue}`);
      }
      return null;
    }
    
    // Looks encrypted - try to decrypt
    try {
      const decrypted = encryptionService.decrypt(roleValue);
      
      if (isValidRole(decrypted)) {
        if (DEV) {
          console.log(`[CookieManager] Decrypted and accepted role: ${decrypted}`);
        }
        return decrypted;
      }
      
      if (DEV) {
        console.warn(`[CookieManager] Rejected invalid decrypted role: ${decrypted}`);
      }
      return null;
    } catch (decryptError) {
      // Decryption failed - log but don't crash
      console.error('[CookieManager] Role decryption failed (non-critical):', 
        decryptError instanceof Error ? decryptError.message : 'Unknown error');
      return null;
    }
  } catch (error) {
    // Catch any unexpected errors and return null
    console.error('[CookieManager] Unexpected error in safeDecryptRole:', error);
    return null;
  }
}

/* =============================================================================
 * Promise-safe wrappers for Next.js cookies API
 * ============================================================================= */

async function getReadCookieStore(): Promise<CookieStoreRead> {
  const store = await Promise.resolve(cookies() as unknown);
  return store as CookieStoreRead;
}

async function getWriteCookieStore(): Promise<CookieStoreWrite> {
  const store = (await Promise.resolve(cookies() as unknown)) as CookieStoreWrite;

  if (typeof store.set !== 'function' || typeof store.delete !== 'function') {
    throw new Error(
      'Cookie store is read-only in this context. ' +
        'Call CookieManager methods only from a Server Action or Route Handler.'
    );
  }

  return store;
}

async function getRequestHeaders(): Promise<HeaderStoreRead> {
  const hdrs = await Promise.resolve(headers() as unknown);
  return hdrs as HeaderStoreRead;
}

/* =============================================================================
 * COOKIE OPTIONS - SAFE AND CONSISTENT
 * ============================================================================= */

function buildCookieOptions(maxAgeSeconds?: number, isClientReadable = false): CookieOptions {
  const base: CookieOptions = {
    httpOnly: !isClientReadable,
    secure: ENV_CONFIG.USE_SECURE_COOKIES,
    sameSite: 'lax' as const,
    path: ENV_CONFIG.BASE_PATH,
    priority: 'high' as const,
    maxAge: maxAgeSeconds ?? SECURITY_CONFIG.ACCESS_TOKEN_MAX_AGE,
  };

  // ✅ FIX: Consistent domain logic
  // Only set domain if we have a valid one that's not localhost
  if (ENV_CONFIG.COOKIE_DOMAIN && ENV_CONFIG.COOKIE_DOMAIN !== 'localhost') {
    // For production, use exact domain (not .domain.com) for better security
    base.domain = ENV_CONFIG.COOKIE_DOMAIN;
  }

  // ✅ FIX: Never set domain for localhost in development
  if (DEV && ENV_CONFIG.COOKIE_DOMAIN === 'localhost') {
    delete base.domain; // host-only cookies for localhost
  }

  return base;
}

function buildVolatileCookieOptions(): CookieOptions {
  const base: CookieOptions = {
    httpOnly: false,
    secure: ENV_CONFIG.USE_SECURE_COOKIES,
    sameSite: 'lax' as const,
    path: ENV_CONFIG.BASE_PATH,
    maxAge: 60, // 1 minute
  };

  if (ENV_CONFIG.COOKIE_DOMAIN && ENV_CONFIG.COOKIE_DOMAIN !== 'localhost') {
    base.domain = ENV_CONFIG.COOKIE_DOMAIN;
  }

  return base;
}

function deleteCookie(store: CookieStoreWrite, name: CookieName) {
  try {
    store.delete(name);
  } catch {
    // ignore
  }
}

/* =============================================================================
 * COOKIE UPDATE COOLDOWN - PREVENT RAPID UPDATES
 * ============================================================================= */

async function shouldUpdateCookies(sessionId: string): Promise<boolean> {
  try {
    const store = await getReadCookieStore();
    const lastUpdateStr = store.get(TRANSIENT_COOKIE_NAMES.LAST_COOKIE_UPDATE)?.value;
    
    if (!lastUpdateStr) return true;
    
    const lastUpdate = parseInt(lastUpdateStr, 10);
    const now = nowMs();
    
    return now - lastUpdate >= SECURITY_CONFIG.COOKIE_UPDATE_COOLDOWN_MS;
  } catch {
    return true; // If we can't check, allow update
  }
}

async function recordCookieUpdate(): Promise<void> {
  try {
    const store = await getWriteCookieStore();
    store.set(
      TRANSIENT_COOKIE_NAMES.LAST_COOKIE_UPDATE,
      nowMs().toString(),
      buildVolatileCookieOptions()
    );
  } catch {
    // Ignore errors
  }
}

/* =============================================================================
 * MAIN COOKIE MANAGER CLASS
 * ============================================================================= */

class CookieManagement {
  private static instance: CookieManagement | undefined;

  private refreshAttempts = new Map<string, number>();
  private lastRefreshAttempt = new Map<string, number>();
  private lastCookieClear = new Map<string, number>();

  static getInstance(): CookieManagement {
    if (!CookieManagement.instance) {
      CookieManagement.instance = new CookieManagement();
    }
    return CookieManagement.instance;
  }

  private logSecurityEvent(event: string, data: Record<string, unknown>) {
    if (!ENV_CONFIG.LOG_SECURITY_EVENTS) return;

    const safe: Record<string, unknown> = { ...data };
    if (safe.accessToken) safe.accessToken = mask(String(safe.accessToken));
    if (safe.refreshToken) safe.refreshToken = mask(String(safe.refreshToken));
    if (safe.userId) safe.userId = '***';

    console.log(`[SECURITY][CookieManager] ${event}`, safe);
  }

  /* ===========================================================================
   * SAFE SET AUTH COOKIES - ENCRYPT ROLE COOKIE
   * =========================================================================== */

  async setAuthCookies(params: {
    userId: string | number;
    accessToken: string;
    refreshToken: string;
    sessionId?: string;
    deviceId?: string;
    deviceName?: string;
    rememberMe: boolean;
    expiresIn: number;
    role: UserRole;
  }): Promise<{ sessionId: string; deviceId: string }> {
    const store = await getWriteCookieStore();
    const hdrs = await getRequestHeaders();

    // ✅ FIX: Validate required parameters
    if (!params.accessToken || !params.refreshToken || !params.userId) {
      throw new Error('Missing required parameters for setAuthCookies');
    }

    // ✅ FIX: Validate role is allowed
    if (!isValidRole(params.role)) {
      throw new Error(`Invalid role: ${params.role}. Allowed: ${ALLOWED_ROLES.join(', ')}`);
    }

    // ✅ FIX: Generate consistent IDs
    const sessionId = params.sessionId || randomHexChars(SECURITY_CONFIG.SESSION_ID_LENGTH);
    const deviceId = params.deviceId || randomHexChars(SECURITY_CONFIG.DEVICE_ID_LENGTH);

    // ✅ FIX: Clamp token expiration times
    const accessMaxAge = clampSeconds(
      params.expiresIn || SECURITY_CONFIG.ACCESS_TOKEN_MAX_AGE,
      60,
      24 * 60 * 60
    );

    const refreshMaxAge = params.rememberMe
      ? SECURITY_CONFIG.REFRESH_TOKEN_MAX_AGE_REMEMBER
      : SECURITY_CONFIG.REFRESH_TOKEN_MAX_AGE;

    // ✅ FIX: Encrypt user ID
    const encryptedUserId = encryptionService.encrypt(String(params.userId));
    
    // ✅ FIX: ENCRYPT ROLE (NEW BEHAVIOR)
    const encryptedRole = encryptionService.encrypt(params.role);

    // Gather metadata
    const ipAddress =
      parseForwardedFor(hdrs.get('x-forwarded-for')) ||
      hdrs.get('x-real-ip') ||
      'unknown';

    const userAgent = hdrs.get('user-agent') || 'unknown';
    const deviceName = sanitizeDeviceName(params.deviceName);

    const meta: SessionMetadata = {
      id: sessionId,
      userId: String(params.userId),
      createdAt: nowMs(),
      lastActive: nowMs(),
      deviceId,
      deviceName,
      ipAddress,
      userAgent,
      expiresAt: nowMs() + accessMaxAge * 1000,
      isValid: true,
      role: params.role,
    };

    const encryptedMeta = encryptionService.encrypt(JSON.stringify(meta));

    log('Setting auth cookies', {
      cookieDomain: ENV_CONFIG.COOKIE_DOMAIN || '(host-only)',
      userId: String(params.userId),
      role: params.role,
      roleEncrypted: true, // ✅ NEW: Role is now encrypted
      accessMaxAge,
      refreshMaxAge,
      sessionId,
      deviceId,
      deviceName,
    });

    // ✅ FIX: Build cookie options once for consistency
    const authCookieOptions = buildCookieOptions(accessMaxAge);
    const refreshCookieOptions = buildCookieOptions(refreshMaxAge);
    const clientCookieOptions = buildCookieOptions(accessMaxAge, true);

    // Set auth cookies with validated values
    store.set(COOKIE_NAMES.ACCESS, params.accessToken, authCookieOptions);
    store.set(COOKIE_NAMES.USER_ID, encryptedUserId, authCookieOptions);
    store.set(COOKIE_NAMES.ROLE, encryptedRole, authCookieOptions); // ✅ ENCRYPTED
    store.set(COOKIE_NAMES.SESSION_ID, sessionId, authCookieOptions);
    store.set(COOKIE_NAMES.DEVICE_ID, deviceId, authCookieOptions);
    store.set(COOKIE_NAMES.DEVICE_NAME, deviceName, authCookieOptions);
    store.set(COOKIE_NAMES.REMEMBER, params.rememberMe ? 'true' : 'false', authCookieOptions);
    store.set(COOKIE_NAMES.SESSION_META, encryptedMeta, authCookieOptions);
    store.set(COOKIE_NAMES.SESSION_EXPIRES_AT, String(meta.expiresAt), authCookieOptions);

    // Refresh token with longer expiry
    store.set(COOKIE_NAMES.REFRESH, params.refreshToken, refreshCookieOptions);

    // Client-readable auth flag
    store.set(COOKIE_NAMES.AUTH_STATE, 'authenticated', clientCookieOptions);

    // Reset transient error counters
    await this.resetTransientErrorCounters(store);
    await recordCookieUpdate();

    this.logSecurityEvent('SET_COOKIES', {
      sessionId,
      deviceId,
      rememberMe: params.rememberMe,
      cookieDomain: ENV_CONFIG.COOKIE_DOMAIN || '(host-only)',
      roleEncrypted: true,
    });

    return { sessionId, deviceId };
  }

  /* ===========================================================================
   * TRANSIENT ERROR HANDLING - NO COOKIE CLEARING
   * =========================================================================== */

  private async resetTransientErrorCounters(store: CookieStoreWrite): Promise<void> {
    try {
      deleteCookie(store, TRANSIENT_COOKIE_NAMES.TRANSIENT_ERROR_COUNT as any);
      deleteCookie(store, TRANSIENT_COOKIE_NAMES.LAST_TRANSIENT_ERROR as any);
      deleteCookie(store, TRANSIENT_COOKIE_NAMES.RETRY_AFTER as any);
    } catch {
      // Ignore cleanup errors
    }
  }

  async recordTransientError(error: unknown): Promise<{
    shouldRetry: boolean;
    retryAfter?: number;
    errorCount: number;
  }> {
    const store = await getWriteCookieStore();
    const classification = classifyError(error);
    
    // Only record transient errors
    if (!classification.isTransient) {
      return { shouldRetry: false, errorCount: 0 };
    }

    const currentCountStr = store.get(TRANSIENT_COOKIE_NAMES.TRANSIENT_ERROR_COUNT)?.value || '0';
    let errorCount = parseInt(currentCountStr, 10) || 0;
    
    errorCount++;
    
    store.set(
      TRANSIENT_COOKIE_NAMES.TRANSIENT_ERROR_COUNT,
      errorCount.toString(),
      buildVolatileCookieOptions()
    );
    
    store.set(
      TRANSIENT_COOKIE_NAMES.LAST_TRANSIENT_ERROR,
      Date.now().toString(),
      buildVolatileCookieOptions()
    );

    const shouldRetry = errorCount <= SECURITY_CONFIG.TRANSIENT_ERROR_MAX_RETRIES;
    const retryAfter = shouldRetry 
      ? SECURITY_CONFIG.TRANSIENT_ERROR_BACKOFF_MS * errorCount
      : undefined;

    if (shouldRetry && retryAfter) {
      store.set(
        TRANSIENT_COOKIE_NAMES.RETRY_AFTER,
        (Date.now() + retryAfter).toString(),
        buildVolatileCookieOptions()
      );
    }

    log('Recorded transient error', {
      errorCode: classification.code,
      errorCount,
      shouldRetry,
      retryAfter,
      maxRetries: SECURITY_CONFIG.TRANSIENT_ERROR_MAX_RETRIES,
    });

    return { shouldRetry, retryAfter, errorCount };
  }

  async getTransientErrorInfo(): Promise<{
    errorCount: number;
    lastErrorTime?: number;
    retryAfter?: number;
  }> {
    const store = await getReadCookieStore();
    
    const errorCountStr = store.get(TRANSIENT_COOKIE_NAMES.TRANSIENT_ERROR_COUNT)?.value || '0';
    const lastErrorStr = store.get(TRANSIENT_COOKIE_NAMES.LAST_TRANSIENT_ERROR)?.value;
    const retryAfterStr = store.get(TRANSIENT_COOKIE_NAMES.RETRY_AFTER)?.value;
    
    const errorCount = parseInt(errorCountStr, 10) || 0;
    const lastErrorTime = lastErrorStr ? parseInt(lastErrorStr, 10) : undefined;
    const retryAfter = retryAfterStr ? parseInt(retryAfterStr, 10) - Date.now() : undefined;

    return {
      errorCount,
      lastErrorTime,
      retryAfter: retryAfter && retryAfter > 0 ? retryAfter : undefined,
    };
  }

  /* ===========================================================================
   * SAFE CLEAR METHODS - ONLY CLEAR WHEN NECESSARY
   * =========================================================================== */

  /**
   * ✅ FIX: Only clear cookies on confirmed auth invalidation (401/419)
   * This is called when backend explicitly returns 401/419
   */
  async clearAuthCookiesHard(options?: { preserveDevice?: boolean }): Promise<void> {
    const store = await getWriteCookieStore();
    const sessionId = await this.getCurrentSessionId();

    // Check if we recently cleared cookies for this session
    if (sessionId) {
      const lastClear = this.lastCookieClear.get(sessionId) || 0;
      const now = nowMs();
      
      if (now - lastClear < SECURITY_CONFIG.REFRESH_COOLDOWN_MS) {
        warn('Skipping duplicate hard cookie clear', {
          sessionId: mask(sessionId),
          timeSinceLastClear: now - lastClear,
        });
        return;
      }
      
      this.lastCookieClear.set(sessionId, now);
    }

    const base: CookieName[] = [
      COOKIE_NAMES.ACCESS,
      COOKIE_NAMES.REFRESH,
      COOKIE_NAMES.USER_ID,
      COOKIE_NAMES.ROLE,
      COOKIE_NAMES.SESSION_ID,
      COOKIE_NAMES.SESSION_META,
      COOKIE_NAMES.SESSION_EXPIRES_AT,
      COOKIE_NAMES.AUTH_STATE,
    ];

    const device: CookieName[] = [
      COOKIE_NAMES.DEVICE_ID,
      COOKIE_NAMES.DEVICE_NAME,
      COOKIE_NAMES.REMEMBER,
    ];

    const toClear = options?.preserveDevice ? base : [...base, ...device];

    log('Hard clearing auth cookies', { 
      preserveDevice: options?.preserveDevice ?? false,
      count: toClear.length,
      sessionId: sessionId ? mask(sessionId) : 'none',
      reason: '401/419 auth invalidation from backend',
    });

    for (const name of toClear) {
      deleteCookie(store, name);
    }

    await this.resetTransientErrorCounters(store);

    this.logSecurityEvent('HARD_LOGOUT', { 
      preserveDevice: options?.preserveDevice ?? false,
      sessionId: sessionId ? mask(sessionId) : 'none',
    });
  }

  /**
   * ✅ FIX: Soft clear - only clears non-essential cookies for transient errors
   * This preserves auth tokens for retry after network/timeout errors
   */
  async clearAuthCookiesSoft(): Promise<void> {
    const store = await getWriteCookieStore();

    log('Soft clearing non-essential cookies', { 
      action: 'Preserving auth tokens for retry',
      reason: 'Transient error (timeout/network/server)',
    });

    // Only clear client-readable state, not auth tokens
    deleteCookie(store, COOKIE_NAMES.AUTH_STATE);

    await this.resetTransientErrorCounters(store);

    this.logSecurityEvent('SOFT_LOGOUT', { 
      note: 'Auth tokens preserved for retry after transient error',
    });
  }

  /**
   * Legacy method - defaults to soft clear for safety
   * @deprecated Use clearAuthCookiesHard() or clearAuthCookiesSoft() explicitly
   */
  async clearAuthCookies(options?: { preserveDevice?: boolean }): Promise<void> {
    warn('clearAuthCookies called without specifying hard/soft. Defaulting to soft.', {
      recommendation: 'Use clearAuthCookiesHard() for auth invalidation or clearAuthCookiesSoft() for transient errors',
    });
    
    // Default to soft clear to prevent accidental logout on transient errors
    return this.clearAuthCookiesSoft();
  }

  /* ===========================================================================
   * READ METHODS - WITH SAFE ROLE DECRYPTION
   * =========================================================================== */

  async getAuthCookies(): Promise<AuthCookies> {
    const store = await getReadCookieStore();

    const raw: AuthCookies = {
      access_token: store.get(COOKIE_NAMES.ACCESS)?.value,
      refresh_token: store.get(COOKIE_NAMES.REFRESH)?.value,
      user_id: store.get(COOKIE_NAMES.USER_ID)?.value,
      role: store.get(COOKIE_NAMES.ROLE)?.value, // ✅ Can be encrypted or plaintext
      session_id: store.get(COOKIE_NAMES.SESSION_ID)?.value,
      device_id: store.get(COOKIE_NAMES.DEVICE_ID)?.value,
      device_name: store.get(COOKIE_NAMES.DEVICE_NAME)?.value,
      remember_me: store.get(COOKIE_NAMES.REMEMBER)?.value,
      auth_state: store.get(COOKIE_NAMES.AUTH_STATE)?.value,
      session_meta: store.get(COOKIE_NAMES.SESSION_META)?.value,
      session_expires_at: store.get(COOKIE_NAMES.SESSION_EXPIRES_AT)?.value,
    };

    if (DEV) {
      const roleType = raw.role ? (raw.role.includes(':') ? 'encrypted' : 'plaintext') : 'none';
      log('Retrieved auth cookies', {
        hasAccess: Boolean(raw.access_token),
        hasRefresh: Boolean(raw.refresh_token),
        hasUserId: Boolean(raw.user_id),
        hasRole: Boolean(raw.role),
        roleType,
        hasSessionId: Boolean(raw.session_id),
        authState: raw.auth_state ?? 'none',
      });
    }

    // Check for corrupted session metadata
    const claimsAuth = raw.auth_state === 'authenticated';
    if (claimsAuth && raw.session_meta && !looksLikeGcmEncrypted(raw.session_meta)) {
      warn('Corrupted session metadata detected', { 
        action: 'Will clear on next write attempt',
        sessionId: raw.session_id ? mask(raw.session_id) : 'none',
      });
      // Don't clear here - let the write context handle it
    }

    return raw;
  }

  /**
   * ✅ NEW: Get decrypted role with backward compatibility
   * Never throws - returns null on any error
   */
  async getDecryptedRole(): Promise<string | null> {
    try {
      const store = await getReadCookieStore();
      const roleValue = store.get(COOKIE_NAMES.ROLE)?.value;
      
      if (!roleValue) {
        return null;
      }
      
      return safeDecryptRole(roleValue);
    } catch (error) {
      // NEVER THROW - log and return null
      console.error('[CookieManager] Error getting decrypted role:', error);
      return null;
    }
  }

  /**
   * ✅ NEW: Check if user has specific role
   * Never throws - returns false on any error
   */
  async hasRole(role: AllowedRole): Promise<boolean> {
    const userRole = await this.getDecryptedRole();
    return userRole === role;
  }

  /**
   * ✅ NEW: Check if user is admin (owner or admin)
   * Never throws - returns false on any error
   */
  async isAdmin(): Promise<boolean> {
    const userRole = await this.getDecryptedRole();
    return userRole === 'admin' || userRole === 'owner';
  }

  async getSessionMetadata(): Promise<SessionMetadata | null> {
    try {
      const store = await getReadCookieStore();
      const encryptedMeta = store.get(COOKIE_NAMES.SESSION_META)?.value;

      if (!looksLikeGcmEncrypted(encryptedMeta)) return null;

      const decrypted = encryptionService.decrypt(encryptedMeta!);
      const meta = JSON.parse(decrypted) as SessionMetadata;

      if (!meta?.id || !meta?.deviceId || !meta?.expiresAt) return null;

      // Check if session is expired
      if (meta.expiresAt < nowMs()) {
        warn('Session expired', { 
          sessionId: mask(meta.id),
          expiredAt: new Date(meta.expiresAt).toISOString(),
        });
        
        // Only clear if we have write access
        try {
          const writeStore = await getWriteCookieStore();
          await this.clearAuthCookiesHard({ preserveDevice: false });
        } catch {
          // Read-only context, can't clear
        }
        
        return null;
      }

      return meta;
    } catch (e) {
      errLog('Failed to get session metadata', { 
        error: String((e as any)?.message ?? e),
      });
      return null;
    }
  }

  private async getCurrentSessionId(): Promise<string | null> {
    try {
      const store = await getReadCookieStore();
      return store.get(COOKIE_NAMES.SESSION_ID)?.value || null;
    } catch {
      return null;
    }
  }

  /* ===========================================================================
   * UPDATE METHODS - WITH COOLDOWN AND VALIDATION
   * =========================================================================== */

  async updateSessionActivity(): Promise<void> {
    const sessionId = await this.getCurrentSessionId();
    if (!sessionId) return;

    // Check cooldown
    if (!(await shouldUpdateCookies(sessionId))) {
      log('Skipping session activity update - in cooldown', {
        sessionId: mask(sessionId),
      });
      return;
    }

    const meta = await this.getSessionMetadata();
    if (!meta) return;

    meta.lastActive = nowMs();

    const remainingSeconds = clampSeconds(
      (meta.expiresAt - nowMs()) / 1000,
      1,
      24 * 60 * 60
    );

    const store = await getWriteCookieStore();
    store.set(
      COOKIE_NAMES.SESSION_META,
      encryptionService.encrypt(JSON.stringify(meta)),
      buildCookieOptions(remainingSeconds)
    );

    await recordCookieUpdate();

    log('Updated session activity', { 
      sessionId: mask(sessionId),
      remainingSeconds,
    });
  }

  /**
   * ✅ FIX: Update role - encrypts new role value
   * Validates role against allow-list before setting
   */
  async updateRole(role: UserRole): Promise<void> {
    // Validate role is allowed
    if (!isValidRole(role)) {
      throw new Error(`Invalid role: ${role}. Allowed: ${ALLOWED_ROLES.join(', ')}`);
    }

    const sessionId = await this.getCurrentSessionId();
    if (!sessionId) {
      throw new Error('No active session to update role');
    }

    // Check cooldown
    if (!(await shouldUpdateCookies(sessionId))) {
      warn('Skipping role update - in cooldown', {
        sessionId: mask(sessionId),
        role,
      });
      return;
    }

    const store = await getWriteCookieStore();
    
    // ✅ ENCRYPT THE ROLE BEFORE SETTING
    const encryptedRole = encryptionService.encrypt(role);
    store.set(COOKIE_NAMES.ROLE, encryptedRole, buildCookieOptions());

    const meta = await this.getSessionMetadata();
    if (meta) {
      meta.role = role;

      const remainingSeconds = clampSeconds(
        (meta.expiresAt - nowMs()) / 1000,
        1,
        24 * 60 * 60
      );

      store.set(
        COOKIE_NAMES.SESSION_META,
        encryptionService.encrypt(JSON.stringify(meta)),
        buildCookieOptions(remainingSeconds)
      );
    }

    await recordCookieUpdate();

    this.logSecurityEvent('UPDATE_ROLE', { 
      role,
      encrypted: true,
      sessionId: mask(sessionId)
    });
    
    log('Updated user role', { 
      role, 
      encrypted: true,
      sessionId: mask(sessionId) 
    });
  }

  /* ===========================================================================
   * REFRESH ATTEMPT MANAGEMENT
   * =========================================================================== */

  canAttemptRefresh(sessionId: string): boolean {
    const now = nowMs();
    const last = this.lastRefreshAttempt.get(sessionId) || 0;

    // Reset attempts if enough time has passed
    if (now - last > SECURITY_CONFIG.REFRESH_COOLDOWN_MS) {
      this.refreshAttempts.set(sessionId, 0);
    }

    const attempts = this.refreshAttempts.get(sessionId) || 0;
    if (attempts >= SECURITY_CONFIG.MAX_REFRESH_ATTEMPTS) {
      this.logSecurityEvent('REFRESH_RATE_LIMIT_EXCEEDED', { 
        sessionId: mask(sessionId), 
        attempts,
        lastAttempt: new Date(last).toISOString(),
      });
      
      warn('Refresh rate limit exceeded', { 
        sessionId: mask(sessionId), 
        attempts,
        maxAttempts: SECURITY_CONFIG.MAX_REFRESH_ATTEMPTS,
      });
      
      return false;
    }

    return true;
  }

  recordRefreshAttempt(sessionId: string): void {
    const attempts = (this.refreshAttempts.get(sessionId) || 0) + 1;
    this.refreshAttempts.set(sessionId, attempts);
    this.lastRefreshAttempt.set(sessionId, nowMs());
    
    log('Recorded refresh attempt', { 
      sessionId: mask(sessionId), 
      attempts,
      maxAttempts: SECURITY_CONFIG.MAX_REFRESH_ATTEMPTS,
    });
  }

  resetRefreshAttempts(sessionId: string): void {
    this.refreshAttempts.delete(sessionId);
    this.lastRefreshAttempt.delete(sessionId);
    
    log('Reset refresh attempts', { sessionId: mask(sessionId) });
  }

  /* ===========================================================================
   * UTILITY METHODS
   * =========================================================================== */

  async tryGetDecryptedUserId(): Promise<string | undefined> {
    const c = await this.getAuthCookies();
    if (!c.user_id) return undefined;

    if (looksLikeGcmEncrypted(c.user_id)) {
      try {
        return encryptionService.decrypt(c.user_id);
      } catch {
        return undefined;
      }
    }

    return c.user_id;
  }

  async isProbablyAuthenticated(): Promise<boolean> {
    const c = await this.getAuthCookies();
    
    const transientInfo = await this.getTransientErrorInfo();
    const hasTooManyTransientErrors = 
      transientInfo.errorCount > SECURITY_CONFIG.TRANSIENT_ERROR_MAX_RETRIES;
    
    return Boolean(
      c.access_token && 
      c.session_id && 
      (c.auth_state === 'authenticated' || !!c.user_id) &&
      !hasTooManyTransientErrors
    );
  }

  async shouldAllowAccessDespiteTransientErrors(): Promise<{
    allow: boolean;
    reason?: string;
    errorCount: number;
  }> {
    const transientInfo = await this.getTransientErrorInfo();
    const hasAuthCookies = await this.isProbablyAuthenticated();

    if (!hasAuthCookies) {
      return { 
        allow: false, 
        reason: 'No auth cookies', 
        errorCount: transientInfo.errorCount 
      };
    }

    if (transientInfo.errorCount > SECURITY_CONFIG.TRANSIENT_ERROR_MAX_RETRIES) {
      return { 
        allow: false, 
        reason: 'Too many transient errors', 
        errorCount: transientInfo.errorCount 
      };
    }

    if (transientInfo.retryAfter && transientInfo.retryAfter > 0) {
      return { 
        allow: false, 
        reason: `Retry after ${Math.ceil(transientInfo.retryAfter / 1000)}s`, 
        errorCount: transientInfo.errorCount 
      };
    }

    return { allow: true, errorCount: transientInfo.errorCount };
  }

  /**
   * ✅ NEW: Check if we should clear cookies based on error type
   */
  async handleErrorGracefully(error: unknown): Promise<{
    shouldClearCookies: boolean;
    clearType: 'hard' | 'soft' | 'none';
    reason: string;
  }> {
    const classification = classifyError(error);
    
    if (classification.requiresLogout) {
      // Only clear on confirmed auth invalidation
      return {
        shouldClearCookies: true,
        clearType: 'hard',
        reason: `Auth invalidation (${classification.code})`,
      };
    }
    
    if (classification.isTransient) {
      // Record but don't clear cookies for transient errors
      await this.recordTransientError(error);
      return {
        shouldClearCookies: false,
        clearType: 'none',
        reason: `Transient error (${classification.code}) - preserving session`,
      };
    }
    
    // Other errors - don't clear cookies
    return {
      shouldClearCookies: false,
      clearType: 'none',
      reason: `Non-transient error (${classification.code})`,
    };
  }

  /**
   * ✅ NEW: Migrate plaintext role to encrypted format
   * Call this during login or session refresh to normalize cookies
   */
  async migrateRoleToEncrypted(): Promise<boolean> {
    try {
      const store = await getWriteCookieStore();
      const roleValue = store.get(COOKIE_NAMES.ROLE)?.value;
      
      if (!roleValue) {
        return false;
      }
      
      // If already encrypted, nothing to do
      if (roleValue.includes(':')) {
        return false;
      }
      
      // Check if plaintext role is valid
      if (!isValidRole(roleValue)) {
        console.warn(`Cannot migrate invalid role: ${roleValue}`);
        return false;
      }
      
      // Encrypt and update
      const encryptedRole = encryptionService.encrypt(roleValue);
      store.set(COOKIE_NAMES.ROLE, encryptedRole, buildCookieOptions());
      
      log('Migrated role to encrypted format', {
        oldRole: roleValue,
        newRole: roleValue, // Same value, just encrypted
        encrypted: true,
      });
      
      return true;
    } catch (error) {
      console.error('Failed to migrate role to encrypted format:', error);
      return false;
    }
  }
}

/* =============================================================================
 * EXPORT SINGLETON INSTANCE
 * ============================================================================= */

export const CookieManager = CookieManagement.getInstance();
export default CookieManager;